/**
 * Subscriptions Page Loader
 * Pre-loads subscription plans, current subscription, history, and wallet balance
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, loaderCache } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import type {
  SubscriptionPlan,
  CurrentSubscription,
  SubscriptionHistoryItem,
} from '@/modules/subscriptions/pages/types';
import {
  fetchSubscriptionPlans,
  fetchCurrentSubscription,
  fetchSubscriptionHistory,
} from '@/services/subscriptionService';
import { getWalletBalance } from '@/services/walletService';
import {
  mockPlans,
  mockCurrentSubscription,
  mockHistory,
} from '@/modules/subscriptions/pages/data';

export interface SubscriptionsData {
  plans: SubscriptionPlan[];
  currentSubscription: CurrentSubscription | null;
  history: SubscriptionHistoryItem[];
  walletBalance: number;
}

/**
 * Fetch subscriptions data
 */
async function fetchSubscriptionsData(): Promise<SubscriptionsData> {
  const cached = loaderCache.get<SubscriptionsData>('subscriptions');
  if (cached) {
    return cached;
  }

  try {
    const [plans, currentSubscription, historyResponse, walletData] =
      await Promise.all([
        fetchSubscriptionPlans(),
        fetchCurrentSubscription(),
        fetchSubscriptionHistory(),
        getWalletBalance(),
      ]);

    const data: SubscriptionsData = {
      plans,
      currentSubscription,
      history: historyResponse.data,
      walletBalance: walletData.available_balance,
    };

    loaderCache.set('subscriptions', data, 5 * 60 * 1000);
    return data;
  } catch (error) {
    console.warn('Subscriptions API unavailable, using mock data:', error);

    const data: SubscriptionsData = {
      plans: mockPlans,
      currentSubscription: mockCurrentSubscription,
      history: mockHistory,
      walletBalance: 0,
    };

    return data;
  }
}

/**
 * Subscriptions loader function
 */
export async function subscriptionsLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    await requireAuth(args.request);

    const data = await fetchSubscriptionsData();

    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/subscriptions', loadTime, dataSize);

    return data;
  } catch (error) {
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/subscriptions', error as Error);
    throw error;
  }
}
